'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { trackEvent } from '@/lib/analytics';
import type { GatedFeature } from '@/constant/subscription';
import MembershipModal from '@/components/MemberShip/MembershipModal';
import SwitchToParentAccountModal from '@/components/SwitchToParentAccountModal';
import SubjectManagementModal from '@/components/SubjectManagementModal';
import AskParentCheckoutModal from '@/components/Subscription/AskParentCheckoutModal';
import useHasParentAccount from '@/hooks/useHasParentAccount';

interface PaywallModalProps {
  visible: boolean;
  feature: GatedFeature;
  onClose: () => void;
}

export default function PaywallModal({ visible, feature, onClose }: PaywallModalProps) {
  const router = useRouter();
  const [showSwitchParentModal, setShowSwitchParentModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [showAskParentModal, setShowAskParentModal] = useState(false);
  const { hasParent, parentEmail } = useHasParentAccount(visible || showAskParentModal);

  useEffect(() => {
    if (visible) {
      trackEvent('paywall_shown', { feature });
    }
  }, [visible, feature]);

  const handleClose = () => {
    trackEvent('paywall_dismissed', { feature });
    onClose();
  };

  const handleOpenLinkParent = () => {
    trackEvent('paywall_cta_clicked', { feature, destination: 'ask_parent_checkout' });
    onClose();
    setShowAskParentModal(true);
  };

  const handleOpenSwitchParent = () => {
    trackEvent('paywall_cta_clicked', { feature, destination: 'go_to_parent_account' });
    onClose();
    setShowSwitchParentModal(true);
  };

  return (
    <>
      <MembershipModal
        isOpen={visible}
        onClose={handleClose}
        onOpenLinkParent={handleOpenLinkParent}
        onOpenSwitchParent={handleOpenSwitchParent}
        onOpenSubjectManager={() => {
          onClose();
          setShowSubjectModal(true);
        }}
        onUpgradeSelf={() => {
          trackEvent('paywall_cta_clicked', { feature, destination: 'student_upgrade' });
          onClose();
          router.push('/student/upgrade');
        }}
        tier="free"
        gatedFeature={feature}
      />
      <AskParentCheckoutModal
        visible={showAskParentModal}
        onClose={() => setShowAskParentModal(false)}
        sourceFeature={feature}
        connectedParentEmail={parentEmail}
        lockParentEmail={hasParent && Boolean(parentEmail)}
      />
      <SwitchToParentAccountModal
        visible={showSwitchParentModal}
        onClose={() => setShowSwitchParentModal(false)}
      />
      <SubjectManagementModal
        visible={showSubjectModal}
        onClose={() => setShowSubjectModal(false)}
      />
    </>
  );
}
